/**
 * Path-zone map for the Next Multi-Zones split (docs/claude/
 * thin-main-architecture.md). Each entry is a path prefix (locale-stripped)
 * served by its own worker via Cloudflare Workers Routes on the apex.
 *
 * Cross-zone navigations must be plain <a> tags, not next/link: a <Link> into
 * another zone prefetches a foreign build's RSC payload (wasted worker
 * invocation on every viewport-visible render — the navbar renders on every
 * page) and the client router falls back to a hard load anyway. Use
 * isCrossZoneHref() (via <ZoneLink>) to pick the right element.
 *
 * Which zone the current bundle belongs to is baked at build time via
 * NEXT_PUBLIC_ZONE (set in the zone's CI/deploy workflow env; absent = main).
 */

const PATH_ZONES: Record<string, string> = {
	"/tools": "tools",
	"/reports": "reports",
	"/research": "reports",
	"/data": "reports",
	"/employers": "employers",
	"/talent": "employers",
	"/blog": "content",
	"/changelog": "content",
	"/collaboration": "content",
	"/resources": "content",
	"/landing": "content",
	// The app zone (app.lokerdollar.com). Mirrors `appRoute` in
	// apps/app/src/middleware.ts — that regex decides what this zone SERVES,
	// and anything outside it is 307'd to the apex, so these are exactly the
	// paths a link from another zone must NOT soft-navigate into (and, read
	// the other way, /jobs and friends are cross-zone when you are already on
	// the app zone). Keep the two lists in step.
	"/seeker": "app",
	"/applications": "app",
	"/billing": "app",
	"/resume": "app",
	"/saved": "app",
	"/settings": "app",
	"/upgrade": "app",
	"/onboarding": "app",
};

const CURRENT_ZONE = process.env.NEXT_PUBLIC_ZONE || "main";

/** Pseudo-zone for the session-dependent home (see zoneOfHref). */
const HOME_HANDOFF = "home-handoff";

function zoneOfHref(href: string): string {
	// External/hash/query-only hrefs never soft-navigate anyway.
	if (!href.startsWith("/")) return CURRENT_ZONE;
	// Match on the pathname only: `/en/seeker?q=…` must resolve like
	// `/en/seeker` (a query/hash used to defeat both the locale strip and the
	// prefix match, so it read as "main" and got a soft <Link> into the app
	// zone — the "Failed to fetch RSC payload" fallback on the hero chips).
	const pathname = href.split(/[?#]/, 1)[0] ?? href;
	const path = pathname.replace(/^\/(id|en)(?=\/|$)/, "");
	// The bare locale root (and `/`) is a zone hop for a signed-in visitor:
	// the apex 302s it to app.lokerdollar.com/{locale}/seeker, and the app
	// zone serves its own `/{locale}` as a redirect to /seeker. A soft
	// navigation can't follow that across origins, so no zone owns it for
	// <Link> purposes and every bundle hard-navigates to it.
	if (path === "" || path === "/") return HOME_HANDOFF;
	for (const [prefix, zone] of Object.entries(PATH_ZONES)) {
		if (path === prefix || path.startsWith(`${prefix}/`)) return zone;
	}
	return "main";
}

export function isCrossZoneHref(href: string): boolean {
	return zoneOfHref(href) !== CURRENT_ZONE;
}
